import { Fragment, useEffect, useState } from "react";
import axios from "axios";
import moment from "moment";
import { Badge, Button, Col, FloatingLabel, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import PageLayout from "../../../layouts/PageLayout";

const TYPES = [
  { id: 1, name: "Verification" },
  { id: 2, name: "Rescue" },
  { id: 3, name: "Business" },
  { id: 4, name: "Follow Up" },
];

const formatDate = (date) => (date ? moment(date, "YYYY-MM-DD").format("D MMMM YYYY") : "-");

const DistributionRecords = () => {
  const [groups, setGroups] = useState([]);
  const [familiesTotal, setFamiliesTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filters, setFilters] = useState({ type: "", status: "", from_date: "", to_date: "" });
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0, from: 0 });
  const [openGroup, setOpenGroup] = useState(null);
  const [reload, setReload] = useState(0);
  // edit: { mode: "family", id } for one family, { mode: "group", group } for a whole distribution
  const [edit, setEdit] = useState(null);
  const [editData, setEditData] = useState(null);
  const [editForm, setEditForm] = useState({ verifier_id: "", distribution_date: "", followup_status_id: "" });
  const [saving, setSaving] = useState(false);

  const AuthToken = localStorage.getItem("AuthUser");
  axios.defaults.headers.common["Authorization"] = `Bearer ${AuthToken}`;

  // wait for the user to stop typing before hitting the API
  useEffect(() => {
    const delay = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 500);
    return () => clearTimeout(delay);
  }, [search]);

  useEffect(() => {
    const params = { page };
    if (debouncedSearch) params.search = debouncedSearch;
    Object.entries(filters).forEach(([name, value]) => {
      if (value) params[name] = value;
    });

    setLoading(true);
    setError("");
    axios.get(`${process.env.REACT_APP_API_URL}/distribution-groups`, { params })
      .then((res) => {
        const data = res.data.groups;
        setGroups(data.data);
        setFamiliesTotal(res.data.families_total);
        setPagination({
          current_page: data.current_page,
          last_page: data.last_page,
          total: data.total,
          from: data.from || 0,
        });
      })
      .catch((err) => {
        console.error(err);
        setError("An error occurred while fetching distribution records.");
      })
      .finally(() => setLoading(false));
  }, [page, debouncedSearch, filters, reload]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const handleClear = () => {
    setSearch("");
    setDebouncedSearch("");
    setFilters({ type: "", status: "", from_date: "", to_date: "" });
    setPage(1);
  };

  const loadFailed = (err) => {
    console.error(err);
    setEdit(null);
    Swal.fire("Error", "Failed to load distribution", "error");
  };

  const openFamilyEdit = (id) => {
    setEdit({ mode: "family", id });
    setEditData(null);
    axios.get(`${process.env.REACT_APP_API_URL}/distribution-record/${id}`)
      .then((res) => {
        const { record } = res.data;
        setEditData({
          title: `${record.f_number || "-"} — ${record.beneficiary_name || "N/A"} (${record.type || "-"})`,
          type_id: record.type_id,
          verifiers: res.data.verifiers,
          statuses: res.data.statuses,
        });
        setEditForm({
          verifier_id: record.verifier_id || "",
          distribution_date: record.distribution_date || "",
          followup_status_id: record.followup_status_id || "",
        });
      })
      .catch(loadFailed);
  };

  const openGroupEdit = (group) => {
    setEdit({ mode: "group", group });
    setEditData(null);
    axios.get(`${process.env.REACT_APP_API_URL}/distribution-verifiers/${group.type_id}`)
      .then((res) => {
        const verifiers = res.data.verifiers;
        // keep the current assignee selectable even if they lost the permission
        if (!verifiers.some((v) => v.id === group.user_id)) {
          verifiers.push({ id: group.user_id, name: group.assigned_to || "Current user" });
        }
        setEditData({
          title: `${group.families_count} families — ${group.type || "-"}`,
          type_id: group.type_id,
          verifiers,
          statuses: [],
        });
        setEditForm({ verifier_id: group.user_id, distribution_date: group.distribution_date, followup_status_id: "" });
      })
      .catch(loadFailed);
  };

  const closeEdit = () => {
    setEdit(null);
    setEditData(null);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdate = () => {
    if (!editForm.verifier_id) {
      Swal.fire("Warning", "Please select a user", "warning");
      return;
    }
    if (!editForm.distribution_date) {
      Swal.fire("Warning", "Please select distribution date", "warning");
      return;
    }

    const request = edit.mode === "group"
      ? axios.post(`${process.env.REACT_APP_API_URL}/update-distribution-group`, {
        group_date: edit.group.distribution_date,
        type_id: edit.group.type_id,
        user_id: edit.group.user_id,
        verifier_id: editForm.verifier_id,
        distribution_date: editForm.distribution_date,
      })
      : axios.post(`${process.env.REACT_APP_API_URL}/update-distribution-record/${edit.id}`, {
        ...editForm,
        followup_status_id: editForm.followup_status_id || null,
      });

    setSaving(true);
    request
      .then((res) => {
        Swal.fire("Success", res.data.message || "Distribution updated", "success");
        closeEdit();
        setOpenGroup(null);
        setReload((prev) => prev + 1);
      })
      .catch((err) => {
        console.error(err);
        Swal.fire("Error", err.response?.data?.message || "Failed to update distribution", "error");
      })
      .finally(() => setSaving(false));
  };

  return (
    <PageLayout>
      <Row>
        <Col xl={12}>
          <div className="mc-card">
            <div className="mc-breadcrumb">
              <h3 className="mc-breadcrumb-title">Distribution Record</h3>
              <ul className="mc-breadcrumb-list">
                <li className="mc-breadcrumb-item"><Link to="#" style={{ color: "#077834" }}>home</Link></li>
                <li className="mc-breadcrumb-item"><Link to="#" style={{ color: "#077834" }}>Finances</Link></li>
                <li className="mc-breadcrumb-item">Data Distribution</li>
              </ul>
            </div>
          </div>
        </Col>
      </Row>

      <Row className="mb-3 g-3">
        <Col md={4}>
          <FloatingLabel label="Search FSF-ID / Name / CNIC / Contact / User">
            <Form.Control
              type="text"
              placeholder="Search FSF-ID / Name / CNIC / Contact / User"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </FloatingLabel>
        </Col>
        <Col md={2}>
          <FloatingLabel label="Distributed For">
            <Form.Select name="type" value={filters.type} onChange={handleFilterChange}>
              <option value="">All</option>
              {TYPES.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </Form.Select>
          </FloatingLabel>
        </Col>
        <Col md={2}>
          <FloatingLabel label="Status">
            <Form.Select name="status" value={filters.status} onChange={handleFilterChange}>
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
            </Form.Select>
          </FloatingLabel>
        </Col>
        <Col md={2}>
          <FloatingLabel label="From Date">
            <Form.Control type="date" name="from_date" value={filters.from_date} onChange={handleFilterChange} />
          </FloatingLabel>
        </Col>
        <Col md={2}>
          <FloatingLabel label="To Date">
            <Form.Control type="date" name="to_date" value={filters.to_date} onChange={handleFilterChange} />
          </FloatingLabel>
        </Col>
      </Row>

      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="mb-0">Total Distributions: {pagination.total} | Families: {familiesTotal}</h5>
        <Button variant="secondary" size="sm" onClick={handleClear}>Clear</Button>
      </div>

      {error && <p className="text-danger">{error}</p>}

      {loading ? (
        <h5 className="text-center"><Spinner animation="border" variant="primary" /> Loading Data...</h5>
      ) : (
        <Table bordered hover size="sm" responsive>
          <thead>
            <tr>
              <th>#</th>
              <th>Distribution Date</th>
              <th>Distributed For</th>
              <th>Assigned To</th>
              <th>Distributed By</th>
              <th>Families</th>
              <th>Pending</th>
              <th>Completed</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {groups.length > 0 ? (
              groups.map((group, index) => (
                <Fragment key={group.key}>
                  <tr>
                    <td>{pagination.from + index}</td>
                    <td>{formatDate(group.distribution_date)}</td>
                    <td>{group.type || "-"}</td>
                    <td>{group.assigned_to || "-"}</td>
                    <td>{group.distributed_by || "-"}</td>
                    <td>{group.families_count}</td>
                    <td><Badge bg="warning">{group.pending}</Badge></td>
                    <td><Badge bg="success">{group.completed}</Badge></td>
                    <td className="text-nowrap">
                      <Button
                        size="sm"
                        variant="outline-success"
                        className="me-2"
                        onClick={() => setOpenGroup(openGroup === group.key ? null : group.key)}
                      >
                        {openGroup === group.key ? "Hide" : "View"}
                      </Button>
                      <Button size="sm" style={{ backgroundColor: "#077834" }} onClick={() => openGroupEdit(group)}>Edit</Button>
                    </td>
                  </tr>
                  {openGroup === group.key && (
                    <tr>
                      <td colSpan={9} className="bg-light">
                        <Table striped bordered size="sm" responsive className="mb-0">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>FSF-ID</th>
                              <th>Family Name</th>
                              <th>CNIC</th>
                              <th>Contact</th>
                              <th>Current Stage</th>
                              <th>Status</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {group.families.map((record, i) => (
                              <tr key={record.id}>
                                <td>{i + 1}</td>
                                <td>{record.f_number || "-"}</td>
                                <td>{record.beneficiary_name || "N/A"}</td>
                                <td>{record.cnic || "-"}</td>
                                <td>{record.mob || "-"}</td>
                                <td>{record.stage || "-"}</td>
                                <td>
                                  {record.status ? (
                                    <Badge bg={record.status === "Completed" ? "success" : "warning"}>{record.status}</Badge>
                                  ) : "-"}
                                </td>
                                <td>
                                  <Button size="sm" style={{ backgroundColor: "#077834" }} onClick={() => openFamilyEdit(record.id)}>Edit</Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))
            ) : (
              <tr>
                <td colSpan={9} className="text-center">No distribution records found.</td>
              </tr>
            )}
          </tbody>
        </Table>
      )}

      <div className="d-flex justify-content-center align-items-center gap-3 mt-3">
        <Button
          style={{ backgroundColor: "#077834" }}
          disabled={pagination.current_page <= 1}
          onClick={() => setPage(pagination.current_page - 1)}
        >
          Prev
        </Button>
        <span>Page {pagination.current_page} of {pagination.last_page}</span>
        <Button
          style={{ backgroundColor: "#077834" }}
          disabled={pagination.current_page >= pagination.last_page}
          onClick={() => setPage(pagination.current_page + 1)}
        >
          Next
        </Button>
      </div>

      <Modal show={edit !== null} onHide={closeEdit} centered>
        <Modal.Header closeButton>
          <Modal.Title>{edit?.mode === "group" ? "Edit Distribution" : "Edit Family Distribution"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!editData ? (
            <h5 className="text-center"><Spinner animation="border" variant="primary" /> Loading Data...</h5>
          ) : (
            <>
              <p className="mb-3"><strong>{editData.title}</strong></p>
              <FloatingLabel label="Assigned To" className="mb-3">
                <Form.Select name="verifier_id" value={editForm.verifier_id} onChange={handleEditChange}>
                  <option value="">Select user</option>
                  {editData.verifiers.map((verifier) => (
                    <option key={verifier.id} value={verifier.id}>{verifier.name}</option>
                  ))}
                </Form.Select>
              </FloatingLabel>
              <FloatingLabel label="Distribution Date" className="mb-3">
                <Form.Control type="date" name="distribution_date" value={editForm.distribution_date} onChange={handleEditChange} />
              </FloatingLabel>
              {edit?.mode === "family" && editData.type_id === 4 && (
                <FloatingLabel label="Status" className="mb-3">
                  <Form.Select name="followup_status_id" value={editForm.followup_status_id} onChange={handleEditChange}>
                    <option value="">Default 24 hour status</option>
                    {editData.statuses.map((status) => (
                      <option key={status.id} value={status.id}>{status.name}</option>
                    ))}
                  </Form.Select>
                </FloatingLabel>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeEdit}>Cancel</Button>
          <Button style={{ backgroundColor: "#077834" }} disabled={!editData || saving} onClick={handleUpdate}>
            {saving ? "Saving..." : "Update"}
          </Button>
        </Modal.Footer>
      </Modal>
    </PageLayout>
  );
};

export default DistributionRecords;
